import type { Task } from '@/services/types/client-intake-types';
import { CoverageCategory } from '@/services/types/coverage-types';
import type { CoverageListItem } from '@/services/types/coverage-types';
import { TASK_TEMPLATE_TYPES } from '@/services/types/task-types';

const FIRST_PARTY_CATEGORIES = new Set<CoverageCategory>([
  CoverageCategory.FIRST_PARTY,
]);

/**
 * Find the Verify 1P Coverage task from a list of matter tasks.
 */
export function findVerify1PTask(matterTasks: Task[]): Task | undefined {
  return matterTasks.find(
    (t) =>
      t.template_type === TASK_TEMPLATE_TYPES.COVERAGE_VERIFY_1P_COVERAGE,
  );
}

/**
 * Whether a coverage already has a claim number, meaning the claim was
 * already opened and verification gating should be skipped.
 */
export function hasClaim(coverage: CoverageListItem): boolean {
  return (
    coverage.claims?.some((c) => Boolean(c.claim_identifier?.trim())) ??
    false
  );
}

/**
 * Whether the firm is configured to verify 1P coverage with the client AND at
 * least one first-party coverage still needs a decision.
 *
 * Coverages that already have a claim number are excluded — the claim was
 * already opened so gating is counterproductive.
 */
export function is1PVerificationNeeded(
  coverages: CoverageListItem[],
  verifyWithClientEnabled: boolean,
): boolean {
  if (!verifyWithClientEnabled) return false;

  return coverages.some((c) => {
    const category = c.category;
    return (
      category != null &&
      FIRST_PARTY_CATEGORIES.has(category) &&
      c.client_decision == null &&
      !hasClaim(c)
    );
  });
}
